import Image, { getImageProps, type ImageProps } from 'next/image';
import type { CSSProperties } from 'react';
import { css, cx } from '@/styled-system/css';
import federatedPhotoLoader from '@/src/lib/image/federatedPhotoLoader';
import {
  buildFederatedPhotoAttrs,
  type CcFtSize,
  extractFederatedPhotoInfoFromUrl,
  type FederatedPhotoTreatment,
  isCustomFederatedPhotoTreatment,
} from './federatedPhoto';

const DEFAULT_SIZE: CcFtSize = 768;
const TREATMENT_PARAM = 'fp_treatment';

/** SkeletonBlock's color: the loading affordance the rest of the app uses. */
export const toneBackground = css({ backgroundColor: 'bg.softer' });

export type FederatedPhotoProps = Omit<
  ImageProps,
  'alt' | 'placeholder' | 'src' | 'srcSet' | 'loader' | 'width' | 'height' | 'objectFit' | 'objectPosition'
> & {
  src: string;
  alt: string;
  defaultSize?: CcFtSize;
  treatment?: FederatedPhotoTreatment;
  preserveSource?: boolean;
  placeholder?: 'none' | 'tone';
  priority?: boolean;
  fit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  width?: number;
  height?: number;
};

export type FederatedPhotoPreloadProps = Pick<
  FederatedPhotoProps,
  | 'crossOrigin'
  | 'defaultSize'
  | 'height'
  | 'preserveSource'
  | 'referrerPolicy'
  | 'sizes'
  | 'src'
  | 'treatment'
  | 'width'
> & {
  media: string;
};

type ResolvedFederatedPhotoSource = {
  height: number;
  responsiveAttrs: ReturnType<typeof buildFederatedPhotoAttrs>;
  src: string;
  unoptimized: boolean;
  width: number;
};

function resolveFederatedPhotoSource({
  src,
  defaultSize = DEFAULT_SIZE,
  treatment = 'cc_ft',
  preserveSource = false,
  width,
  height,
}: Pick<
  FederatedPhotoProps,
  'defaultSize' | 'height' | 'preserveSource' | 'src' | 'treatment' | 'width'
>): ResolvedFederatedPhotoSource {
  const responsiveAttrs = preserveSource
    ? null
    : buildFederatedPhotoAttrs(src, defaultSize, [defaultSize], { treatment });
  const isFederatedPhotoSrc = responsiveAttrs !== null;
  const resolvedWidth = width ?? defaultSize;

  return {
    height: height ?? Math.round(resolvedWidth * 0.75),
    responsiveAttrs,
    src: annotateSrcWithTreatment(src, treatment, isFederatedPhotoSrc),
    unoptimized: preserveSource || !isFederatedPhotoSrc,
    width: resolvedWidth,
  };
}

export function FederatedPhotoPreload({
  src,
  sizes,
  defaultSize,
  treatment,
  preserveSource,
  width,
  height,
  media,
  crossOrigin,
  referrerPolicy,
}: FederatedPhotoPreloadProps) {
  const resolved = resolveFederatedPhotoSource({
    src,
    defaultSize,
    treatment,
    preserveSource,
    width,
    height,
  });
  const { props } = getImageProps({
    src: resolved.src,
    alt: '',
    loader: federatedPhotoLoader,
    sizes,
    width: resolved.width,
    height: resolved.height,
    unoptimized: resolved.unoptimized,
    crossOrigin,
    referrerPolicy,
  });

  return (
    <link
      rel="preload"
      as="image"
      href={props.srcSet ? undefined : props.src}
      imageSrcSet={props.srcSet}
      imageSizes={props.sizes}
      media={media}
      fetchPriority="high"
      crossOrigin={props.crossOrigin}
      referrerPolicy={props.referrerPolicy}
    />
  );
}

/**
 * Server-compatible Federated Photo image primitive backed by `next/image`.
 *
 * Next.js/Sharp optimization is disabled at the loader layer — the actual
 * srcset is built by next/image driving the per-image custom loader at
 * `src/lib/image/federatedPhotoLoader.ts`. The widths next/image asks for come
 * from `next.config.ts` `deviceSizes`, which line up with the Federated Photo
 * `cc_ft_*` renders the photos service publishes natively. Keeping the loader
 * per image leaves Next's default optimizer available for local static assets
 * elsewhere in the app.
 * Explicit treatment maps (`{ defaultTreatment, srcSetTreatments }`) are hinted to the loader
 * through a `fp_treatment` query param the wrapper appends to `src`.
 *
 * The `cc_ft` default works for cards, gallery thumbnails, and other 4:3
 * crops. Use an explicit treatment map for uncropped responsive media.
 *
 * Pass `fit="cover"` for viewport-cropped photos or `fit="contain"` for
 * floor plans and lightbox-style media.
 *
 * Use `preserveSource` for media that already arrives with a purpose-built
 * treatment (floor plans). It maps to `<Image>`'s per-image `unoptimized`
 * flag, so the image is rendered as-is with no srcset transformation.
 *
 * Use `priority` for the likely LCP image. next/image emits its own
 * responsive preload link from the generated srcset; we additionally pin
 * `loading="eager"` and `fetchPriority="high"` on the rendered `<img>` so
 * those hints are visible at parser time.
 *
 * Pass `placeholder="tone"` to paint a neutral loading tone behind the image
 * while the final source loads. Deliberately not the remote low-res
 * Federated Photo render: CSS background images bypass native lazy loading,
 * so a URL placeholder downloads for every rendered card — offscreen
 * included — and duplicates each in-view photo's transfer at a second size
 * (HP-8685).
 *
 * Pass `width` and `height` (intrinsic pixel dimensions, not CSS sizing)
 * when the image's natural aspect ratio differs from the 4:3 default; the
 * browser uses them to reserve aspect-ratio space and prevents CLS.
 *
 * For caller-recoverable image-load failures use
 * `FederatedPhotoWithFallback` (client) — this primitive deliberately
 * does not carry error state so the surfaces that don't need a fallback
 * don't pay the useState hydration cost.
 */
export function FederatedPhoto({
  src,
  alt,
  sizes,
  defaultSize = DEFAULT_SIZE,
  treatment = 'cc_ft',
  preserveSource = false,
  placeholder = 'none',
  priority = false,
  fetchPriority,
  loading,
  fit,
  objectPosition,
  style,
  width,
  height,
  className,
  ...imgProps
}: FederatedPhotoProps) {
  const resolved = resolveFederatedPhotoSource({ src, defaultSize, treatment, preserveSource, width, height });

  const imageClassName = placeholder === 'tone' ? cx(toneBackground, className) : className;
  const imageStyle =
    fit || objectPosition
      ? {
          objectFit: fit,
          objectPosition,
          ...style,
        }
      : style;

  const effectiveFetchPriority = fetchPriority ?? (priority ? 'high' : undefined);
  const effectiveLoading = loading ?? (effectiveFetchPriority === 'high' ? 'eager' : undefined);

  return (
    <Image
      src={resolved.src}
      alt={alt}
      loader={federatedPhotoLoader}
      sizes={sizes}
      width={resolved.width}
      height={resolved.height}
      priority={priority}
      unoptimized={resolved.unoptimized}
      fetchPriority={effectiveFetchPriority}
      loading={effectiveLoading}
      style={imageStyle}
      className={imageClassName}
      {...imgProps}
    />
  );
}

function annotateSrcWithTreatment(
  src: string,
  treatment: FederatedPhotoTreatment,
  isFederatedPhotoSrc: boolean,
): string {
  if (treatment === 'cc_ft' || !isFederatedPhotoSrc) return src;
  if (!extractFederatedPhotoInfoFromUrl(src)) return src;
  try {
    const url = new URL(src);
    const value = isCustomFederatedPhotoTreatment(treatment)
      ? `custom:${encodeURIComponent(JSON.stringify(treatment))}`
      : treatment;
    url.searchParams.set(TREATMENT_PARAM, value);
    return url.toString();
  } catch {
    return src;
  }
}
